import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";
import {JwtHelperService} from '@auth0/angular-jwt';
import {AUTH_TOKEN_KEY, LOGIN_TYPE_KEY} from '@shared/constants/storage-keys';
import {AuthConfig, OAuthService} from "angular-oauth2-oidc";
import {BehaviorSubject, from, tap} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {environment} from "../../../environments/environment";
import {LoginType} from '../models/login-type';
import {UsuarioExternoService} from './usuario-externo.service';

export interface Profile {
    name: string;
    email: string;
    picture: string;
}


@Injectable({
    providedIn: 'root',
})
export class AuthService {

    private readonly STORAGE_KEY_LOGIN_TYPE = LOGIN_TYPE_KEY;
    private readonly STORAGE_KEY_TOKEN = AUTH_TOKEN_KEY;
    private readonly uriGovbr: string = environment.api.uriGovbr;
    private readonly baseUrl: string = environment.baseUrl;
    private readonly authStateSubject = new BehaviorSubject<boolean>(false);

    authState$ = this.authStateSubject.asObservable();

    constructor(
        private readonly oauthService: OAuthService,
        private readonly usuarioExternoService: UsuarioExternoService,
        private readonly router: Router,
        private readonly http: HttpClient,
    ) {

        const authConfig: AuthConfig = {
            issuer:
                'http://localhost:8080',
            redirectUri: environment.msal.redirectUri,
            clientId: environment.msal.clientId,
            responseType: 'code',
            scope: 'openid profile email user.read',
            showDebugInformation: false,
            strictDiscoveryDocumentValidation: false,
        };

        this.oauthService.configure(authConfig);

        this.emitAuthStateChange();
    }

    handleLoginRedirection() {
        const loginType = this.getStorageLoginType();

        if (loginType === LoginType.GOV) {
            const code = new URLSearchParams(globalThis.location.search).get('code');
            if (code) {
                console.log('[AuthService] code founded at redirect callback govbr')
                this.handleGovBrCallback(code);
                return;
            }
            console.error('[AuthService] Token não encontrado na resposta do redirect-callback Gov.br');
            return;
        }

        console.info('[AuthService] redirect default auth')
        this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
            if (this.oauthService.hasValidAccessToken()) {
                this.router.navigate(['/']).then(() => console.info('[AuthService] redirected to home'));
            } else {
                this.router.navigate(['/auth/login']).then(() => console.info('[AuthService] redirected to login'));
            }
        });
    }

    logout() {
        if (this.hasStorageLoginType()) {
            const loginType = this.getStorageLoginType();
            if (loginType === LoginType.GOV) {
                this.logoutFromGovBr();
                return;
            }
        }
        this.clearSession();
        this.router.navigate(['/auth/login']).then(() => console.info('[AuthService] redirected to login'));
    }

    logoutFromGovBr(): void {
        console.info('[AuthService] logout from Gov.br')
        const token = this.getStorageToken();
        const headers = new HttpHeaders({
            Authorization: `Bearer ${token}`
        });
        this.http.get(`${this.uriGovbr}/auth/logout-govbr`, {headers}).subscribe({
            next: () => {
                this.clearSession();
                globalThis.location.href = `${environment.uriGovBr}/logout?post_logout_redirect_uri=${this.baseUrl}/api/sisdemanda-publico/gov/auth/logout`
            },
            error: (err) => {
                console.log('[AuthService] logoutFromGovBr error:', err)
            }
        })
    }


    getProfileClaimsFromTokenGovbr(): Profile | null {
        if (this.isAuthenticatedGovBr()) {
            const token = this.getStorageToken();
            if (!token) return null;
            const claims = new JwtHelperService().decodeToken(token);
            if (!claims) return null;
            return {
                name: claims['nome'],
                email: claims['email'],
                picture: '',
            };
        }
        return null;
    }

    isAuthenticated(): boolean {
        return this.isStorageTokenValid();
    }

    isAuthenticatedGovBr(): boolean {
        if (this.hasStorageLoginType() && this.hasStorageToken()) {
            const loginType = this.getStorageLoginType();
            if (loginType !== LoginType.GOV) return false;
            return this.isStorageTokenValid();
        }
        return false;
    }

    isAuthenticatedConcessionaria(): boolean {
        if (this.hasStorageLoginType() && this.hasStorageToken()) {
            return this.getStorageLoginType() === LoginType.CONCESSIONARIA && this.isStorageTokenValid();
        }
        return false;
    }

    getToken(): string | null {
        return this.getStorageToken() || null;
    }

    loginWithGovbr() {
        this.setStorageLoginType(LoginType.GOV);
        const redirectCallbackUrl = `${environment.msal.redirectUri}`;
        console.log('[AuthService] loginWithGovbr redirect');
        globalThis.location.href = `${this.uriGovbr}/auth/login?redirect_uri_front=${encodeURIComponent(redirectCallbackUrl)}`;
    }

    loginWithConcessionaria(
        email: string,
        token: string
    ) {
        this.setStorageLoginType(LoginType.CONCESSIONARIA);
        return this.usuarioExternoService.validateToken(email, token).pipe(
            tap(requestToken => this.setStorageToken(requestToken.token)),
            switchMap(() => this.usuarioExternoService.getUsuarioLogado()),
            switchMap(() => from(this.router.navigate(['/']))),
        );
    }

    setStorageLoginType(loginType: LoginType) {
        sessionStorage.setItem(this.STORAGE_KEY_LOGIN_TYPE, loginType);
    }

    getStorageLoginType(): LoginType | null {
        const loginType = sessionStorage.getItem(this.STORAGE_KEY_LOGIN_TYPE);
        return loginType ? (loginType as LoginType) : null;
    }

    hasStorageLoginType(): boolean {
        return sessionStorage.getItem(this.STORAGE_KEY_LOGIN_TYPE) !== null;
    }

    setStorageToken(token: string) {
        sessionStorage.setItem(this.STORAGE_KEY_TOKEN, token);
        // Marcar que o usuário acabou de fazer login
        sessionStorage.setItem('recent_login', 'true');
        this.emitAuthStateChange();
    }

    clearSession(): void {
        sessionStorage.clear();
        this.usuarioExternoService.clearUsuario();
        this.emitAuthStateChange();
    }

    private emitAuthStateChange(): void {
        const isAuthenticated = this.isStorageTokenValid();
        this.authStateSubject.next(isAuthenticated);
    }

    getStorageToken(): string | null {
        return sessionStorage.getItem(this.STORAGE_KEY_TOKEN);
    }

    isStorageTokenValid(): boolean {
        const token = this.getStorageToken();
        if (!token) return false;
        const jwtHelper = new JwtHelperService();

        // TODO verificar se esta expirando corretamente
        return !jwtHelper.isTokenExpired(token);
    }

    hasStorageToken(): boolean {
        return sessionStorage.getItem(this.STORAGE_KEY_TOKEN) !== null;
    }

    handleGovBrCallback(code: string) {
        console.info('[AuthService] request auth/redirect-callback from Gov.br')
        this.http.post<any>(`${environment.api.uri}gov/auth/token?code=${code}`, {}).subscribe({
            next: (response) => {
                const token = response.token // ||  response.access_token;
                if (token) {
                    this.setStorageToken(token);
                    this.router.navigate(['/']).then(() => console.info('[AuthService] redirected to home'));
                } else {
                    console.error('[AuthService] Token não encontrado na resposta do Gov.br');
                    this.router.navigate(['/auth/login']).then(() => console.info('[AuthService] redirected to login'));
                }
            },
            error: (err) => {
                console.error('[AuthService] Erro no callback Gov.br:', err);
                this.router.navigate(['/auth/login']).then(() => console.info('[AuthService] redirected to login'));
            }
        });
    }

}
